/* script */
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

// Components
import BaseControl from 'shared/ui/components/form-components/BaseControl.vue';
import BaseImage from 'shared/ui/components/BaseImage.vue';
import FilePicker from 'shared/ui/components/form-components/pickers/FilePicker.vue';
import ImageCropper from 'shared/ui/components/editors/images/ImageCropper.vue';
import ZoomedMedia from 'shared/ui/components/modals/ZoomedMedia.vue';

// Utils
import { isArray, omit } from 'lodash';
import PickedFile from 'shared/ui/components/form-components/pickers/picked-file';
import { getImageDimensions } from 'libs/utils/helpers/image';

/**
 * This component is used to pick images and it comes with an optional image cropper.
 *
 * It has all the properties of the BaseControl plus:
 *
 * @vue-prop {String[]} [extensions] valid image extensions for uploading
 * @vue-prop {Boolean} [croppable] whether to show user the cropper or not
 * @vue-prop {Boolean} [multiple] allow multiple files to be selected
 * @vue-prop {Number} [size] maximum file syze in bytes
 * @vue-prop {Number} [maximum] the maximum number of files
 *
 * If cropper is enabled then:
 *
 * @vue-prop {Number} [ratio] the output image w/h ratio
 * @vue-prop {Number} [width] the output image width
 * @vue-prop {Number} [height] the output image height
 * @vue-prop {Number} [maxWidth] the output image max-width
 * @vue-prop {Number} [maxHeight] the output image max-height

 *
 * @category ui.components.form-elements.pickers
 * @exports ImagePicker
 */
const __vue_script__ = {
    name: 'ImagePicker',

    components: { FilePicker, BaseImage, ImageCropper },

    extends: BaseControl,

    props: {
        value: {
            type: [Array, String],
            default: null
        },

        extensions: {
            type: Array,
            default: () => ['gif', 'jpg', 'jpeg', 'png', 'webp']
        },

        croppable: {
            type: Boolean,
            default: true
        },

        size: {
            type: Number,
            default: 0
        },

        multiple: {
            type: Boolean,
            default: false
        },

        maximum: {
            type: Number,
            default: 0
        },

        ratio: {
            type: Number,
            default: null
        },

        modalZoomTitle: {
            type: String,
            default: null
        },

        tip: {
            type: String,
            default: null,
        },
    },

    data() {
        return {
            edits: [],
            completed: []
        };
    },

    computed: {
        /**
         * Pass-through attributes
         * @returns {Object.<String, String>} a list of attributes sets.
         */
        attrs() {
            // Here we remove all the keys that we don't want `image-cropper`
            // to inherit from this very component. All other options and attributes
            // are passed through. Basically an underscore.pluck, but since it has been
            // deprecated we are doing it manually.
            const props = Object.assign(this.$options.propsData, this.$attrs);

            return omit(props, 'value', 'extensions', 'croppable', 'size', 'multiple', 'maximum');
        },

        /**
         * Utils getter for files
         *
         * @returns {PickedFile[]} collected files
         */
        files() {
            return this.innerValue ? Array.from(this.innerValue) : [];
        },

        innerValue: {
            /** @returns {Array<PickedFile>} */
            get() {
                let value = this.value;

                if (typeof value === 'string') {
                    value = [value];
                }

                const files = [];
                if (isArray(value)) {
                    for (const val of value) {
                        let file;

                        if (typeof val === 'string') {
                            const filename = val.split('/').pop();
                            file = new PickedFile([], filename, undefined, `${filename}${Date.now()}`);
                            file.url = val;
                            file.fresh = false;
                            file.validated = true;
                        } else {
                            file = val;
                        }

                        files.push(file);
                    }
                }

                return files;
            },

            /** @param {Array<PickedFile>} files */
            set(files) {
                this.$emit('input', files);
            }
        }
    },

    watch: {
        /**
         * @param {PickedFile[]} newValue the new value
         * @param {PickedFile[]} oldValue the old value
         */
        async innerValue(newValue, oldValue) {
            // Add new files
            for (const file of newValue) {
                file.url = file.url || URL.createObjectURL(file);
                const isNew = file.hasOwnProperty('fresh') ? file.fresh : !this.getFileById(file.id, oldValue);
                const isOfRightRatio = await this.matchesSpecifiedRatio(file);

                if (file.editable && this.croppable && isNew && !isOfRightRatio) {
                    this.$refs.validator.setErrors([this.$i18n.t('validations.image_does_not_fit')]);
                    this.$nextTick(() => (this.openEditor(file)));
                }

                file.fitsRatio = this.ratio ? isOfRightRatio : true;

                if (isNew) {
                    this.$emit('change', file);
                    if (isOfRightRatio) {
                        this.$emit('cropped');
                    }
                }
            }

            // Remove old files
            for (const file of oldValue) {
                const isOld = !this.getFileById(file.id);

                if (isOld) {
                    // File has been removed
                    this.closeEditor(file);
                }
            }
        },

        croppable() {
            for (const file of this.innerValue) {
                if (this.croppable) {
                    if (file.editable && !this.previouslyEdited(file) && !this.editing(file)) {
                        this.$nextTick(() => (this.openEditor(file)));
                    }
                } else {
                    if (this.editing(file)) {
                        this.$nextTick(() => (this.closeEditor(file)));
                    }
                }
            }
        }
    },

    methods: {
        /**
         * Shows the image editor
         *
         * @param {PickedFile} file the file to edit
         */
        openEditor(file) {
            this.getFileById(file.id)._edit = true;
            this.edits.push(file.id);
            this.completed = this.completed.filter(id => id !== file.id);
            this.$emit('edit-change', { edit: true });
        },

        /**
         * Closes the image editor
         *
         * @param {PickedFile} file the file to edit
         */
        closeEditor(file) {
            (this.getFileById(file.id) || {})._edit = false;
            this.edits = this.edits.filter(id => id !== file.id);
            this.$emit('edit-change', { edit: false });

            if (!file.fitsRatio) {
                this.remove(file);
            }
        },

        /**
         * Emits a cropped event only when the user manually clicks on "Apply"
         *
         * @param {PickedFile} file the file element preserve
         */
        async fileCropped(file) {
            if (await this.editComplete(file)) {
                this.$emit('cropped');
            }
        },

        /**
         * Handler for the closing of the cropper, either with the file saved or cancelled
         *
         * @param {PickedFile} file the file on which the cropper closed
         *
         * @returns {Promise<boolean>}
         */
        async editComplete(file) {
            let closeEditor = true;
            if (this.ratio) {
                const isOfRightRatio = await this.matchesSpecifiedRatio(file);
                closeEditor = isOfRightRatio;
                if (!isOfRightRatio) {
                    this.$refs.validator.setErrors([this.$i18n.t('validations.image_does_not_fit')]);
                }
            }

            if (closeEditor) {
                this.completed.push(file.id);
                this.closeEditor(file);
                this.$refs.uploader.replace(file);
            }

            return closeEditor;
        },

        /**
         * @param {PickedFile} file the file to check whether it is being edited
         *
         * @returns {boolean} whether the file is being edited or not
         */
        editing(file) {
            return this.edits.includes(file.id);
        },

        /**
         * @param {PickedFile} file the file to check whether it has been edited in the past
         *
         * @returns {boolean} whether the file has been edited or not yet
         */
        previouslyEdited(file) {
            return this.completed.includes(file.id);
        },

        /**
         * Removes a file from the value.
         *
         * @param {PickedFile} file the file to remove
         * @param {any} [clickEvent] present if this was explicitly called by the user
         *
         */
        remove(file, clickEvent) {
            this.completed = this.completed.filter(id => id !== file.id);
            this.edits = this.edits.filter(id => id !== file.id);

            // This is calling a method in FilePicker that takes a "emit" boolean as 3rd argument
            // we only want to emit "removed" if user explicitly removed the image
            this.$refs.uploader?.remove(file, undefined, Boolean(clickEvent));
        },

        /**
         * Opens a modal with a bigger version of the image
         *
         * @param {PickedFile} file the file to zoom in
         */
        zoom(file) {
            this.$modal.show(ZoomedMedia, {
                media: file,
                title: this.modalZoomTitle
            }, {
                classes: 'large',
                height: 'auto',
                scrollable: true
            });
        },

        /**
         * Given an ID this method returns the corresponding file
         * in the value set.
         *
         * @param {String} id the file ID
         * @param {FileList} [fileList] an optional file list to lookup from
         *
         * @returns {File|undefined} the corresponding file or undefined
         *
         * @private
         */
        getFileById(id, fileList) {
            const fl = fileList ? Array.from(fileList) : this.files;

            return fl.find(f => f.id === id);
        },

        /**
         * Given a file this method checks if the corresponding image
         * fits the configured ratio.
         *
         * @param {PickedFile} file the image file
         *
         * @returns {Promise<boolean>} whether the file respects the given ratio.
         *
         * @private
         */
        async matchesSpecifiedRatio(file) {
            if (!this.ratio) {
                return false;
            }

            try {
                const { width, height } = await getImageDimensions(file.url);

                const uploadedImgRatio = (width / height).toFixed(5);
                const expectedRatio = this.ratio.toFixed(5);

                return uploadedImgRatio === expectedRatio;
            } catch (error) {
                console.error('[ImagePicker] Unable to determine image ratio', error.message);
                return false;
            }
        },

        onPreviewImageError(event) {
            console.warn('[ImagePicker] Could not load image:', event);
            this.innerValue = [];
        }
    }
};

/* template */
var __vue_render__ = function () {
  var _vm = this
  var _h = _vm.$createElement
  var _c = _vm._self._c || _h
  return _c("validation-provider", {
    ref: "validator",
    attrs: { rules: "image_cropping_completed", slim: true },
    scopedSlots: _vm._u([
      {
        key: "default",
        fn: function (ref) {
          var errors = ref.errors
          return [
            _c(
              "control-decorator",
              {
                staticClass: "image-picker",
                attrs: {
                  dependant: _vm.dependant,
                  label: _vm.label,
                  name: _vm.name,
                  hint: _vm.disabled ? "" : _vm.hint,
                  "hint-after-label": true,
                  errors: errors,
                  uid: _vm.uid,
                  tip: _vm.tip,
                  warning: _vm.warning,
                },
              },
              [
                _c(
                  "div",
                  {
                    directives: [
                      {
                        name: "show",
                        rawName: "v-show",
                        value: _vm.innerValue,
                        expression: "innerValue",
                      },
                    ],
                    staticClass: "images listing-grid",
                  },
                  _vm._l(_vm.innerValue, function (file, index) {
                    return _c(
                      "div",
                      { key: index + "-" + file.id, staticClass: "image" },
                      [
                        _c(
                          "div",
                          {
                            directives: [
                              {
                                name: "show",
                                rawName: "v-show",
                                value: !_vm.editing(file),
                                expression: "!editing(file)",
                              },
                            ],
                            staticClass: "image-preview",
                            on: {
                              click: function ($event) {
                                return _vm.zoom(file)
                              },
                            },
                          },
                          [
                            _c("div", { staticClass: "toolbar" }, [
                              _c("div", { staticClass: "btn-group" }, [
                                !_vm.disabled
                                  ? _c(
                                      "a",
                                      {
                                        directives: [
                                          {
                                            name: "tooltip",
                                            rawName: "v-tooltip",
                                            value: _vm.$t("general.remove"),
                                            expression: "$t('general.remove')",
                                          },
                                        ],
                                        staticClass:
                                          "has-action btn btn-mini btn-icon single remove",
                                        on: {
                                          click: function ($event) {
                                            $event.stopPropagation()
                                            return _vm.remove(file, $event)
                                          },
                                        },
                                      },
                                      [_c("i", { staticClass: "ic-trash" })]
                                    )
                                  : _vm._e(),
                                _vm.croppable && file.editable
                                  ? _c(
                                      "a",
                                      {
                                        directives: [
                                          {
                                            name: "tooltip",
                                            rawName: "v-tooltip",
                                            value: _vm.$t("general.edit"),
                                            expression: "$t('general.edit')",
                                          },
                                        ],
                                        staticClass:
                                          "has-action btn btn-mini btn-icon single edit",
                                        on: {
                                          click: function ($event) {
                                            $event.stopPropagation()
                                            return _vm.openEditor(file)
                                          },
                                        },
                                      },
                                      [_c("i", { staticClass: "ic-edit" })]
                                    )
                                  : _vm._e(),
                              ]),
                            ]),
                            _c("base-image", {
                              attrs: {
                                src: _vm.$utils.url.appendCacheBuster(file.url),
                                alt: file.filename,
                              },
                              on: { error: _vm.onPreviewImageError },
                            }),
                          ],
                          1
                        ),
                        _vm.croppable && file.editable && _vm.editing(file)
                          ? _c(
                              "image-cropper",
                              _vm._b(
                                {
                                  attrs: {
                                    uid: file.id,
                                    src: file.url,
                                    name: file.name,
                                    type: file.type,
                                  },
                                  on: {
                                    cropped: _vm.fileCropped,
                                    cancel: function ($event) {
                                      return _vm.closeEditor(file)
                                    },
                                  },
                                },
                                "image-cropper",
                                _vm.attrs,
                                false
                              )
                            )
                          : _vm._e(),
                      ],
                      1
                    )
                  }),
                  0
                ),
                _c("file-picker", {
                  ref: "uploader",
                  attrs: {
                    name: _vm.name,
                    extensions: _vm.extensions,
                    multiple: _vm.multiple,
                    maximum: _vm.maximum,
                    disabled: _vm.disabled,
                    size: _vm.size,
                  },
                  on: {
                    removed: function (file) {
                      return _vm.$emit("removed", file)
                    },
                  },
                  model: {
                    value: _vm.innerValue,
                    callback: function ($$v) {
                      _vm.innerValue = $$v
                    },
                    expression: "innerValue",
                  },
                }),
              ],
              1
            ),
          ]
        },
      },
    ]),
  })
}
var __vue_staticRenderFns__ = []
__vue_render__._withStripped = true

  /* style */
  const __vue_inject_styles__ = undefined
  /* scoped */
  const __vue_scope_id__ = undefined
  /* module identifier */
  const __vue_module_identifier__ = undefined
  /* functional template */
  const __vue_is_functional_template__ = false
  /* component normalizer */
  function __vue_normalize__(
    template, style, script,
    scope, functional, moduleIdentifier, shadowMode,
    createInjector, createInjectorSSR, createInjectorShadow
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {}

    // For security concerns, we use only base name in production mode.
    component.__file = "apps/shared/src/ui/components/form-components/pickers/ImagePicker.vue"

    if (!component.render) {
      component.render = template.render
      component.staticRenderFns = template.staticRenderFns
      component._compiled = true

      if (functional) component.functional = true
    }

    component._scopeId = scope

    if (false) {
      let hook
      if (false) {
        // In SSR.
        hook = function(context) {
          // 2.3 injection
          context =
            context || // cached call
            (this.$vnode && this.$vnode.ssrContext) || // stateful
            (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext) // functional
          // 2.2 with runInNewContext: true
          if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
            context = __VUE_SSR_CONTEXT__
          }
          // inject component styles
          if (style) {
            style.call(this, createInjectorSSR(context))
          }
          // register component module identifier for async chunk inference
          if (context && context._registeredComponents) {
            context._registeredComponents.add(moduleIdentifier)
          }
        }
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        component._ssrRegister = hook
      }
      else if (style) {
        hook = shadowMode 
          ? function(context) {
              style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot))
            }
          : function(context) {
              style.call(this, createInjector(context))
            }
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context)
            return originalRender(h, context)
          }
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook]
        }
      }
    }

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/__vue_normalize__(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  )

  export default __vue_component__